import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, IconButton, Box, Typography, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

const s3Axios = axios.create({ withCredentials: false, headers: {} });

const AttachmentPreviewDialog = ({ open, onClose, file }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    setBlobUrl(null);
    if (!file?.url) return;

    const ext = (file.name || file.originalName || "").split(".").pop()?.toLowerCase();
    const isPDF = ext === "pdf" || file.type === "application/pdf";
    if (!isPDF) return;

    let objectUrl;
    s3Axios
      .get(file.url, { responseType: "blob", params: { t: Date.now() } })
      .then((res) => {
        objectUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setBlobUrl(objectUrl);
      })
      .catch(() => setBlobUrl(null));

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [file]);

  if (!file) return null;

  const fileName = file.originalName || file.name || "Attachment";
  const ext = fileName.split(".").pop()?.toLowerCase();
  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext) || file.type?.startsWith("image/");
  const isPDF = ext === "pdf" || file.type === "application/pdf";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { bgcolor: "#0f172a", borderRadius: 3, m: { xs: 1, sm: 2 }, overflow: "hidden" } }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <Typography variant="body2" sx={{ color: "white", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
          {fileName}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton component="a" href={file.url} download={fileName} target="_blank" rel="noopener noreferrer"
            sx={{ color: "white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: "white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "70vh", p: 2 }}>
        {isImage && (
          <Box component="img" src={file.url} alt={fileName}
            sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 2 }}
          />
        )}

        {isPDF && (
          blobUrl
            ? <iframe src={blobUrl} title={fileName} style={{ width: "100%", height: "70vh", border: "none", borderRadius: 8 }} />
            : <CircularProgress sx={{ color: "white" }} />
        )}

        {!isImage && !isPDF && (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h1" sx={{ fontSize: 72 }}>📄</Typography>
            <Typography sx={{ color: "white", opacity: 0.7, mt: 1 }}>
              Preview not available — use the download button above.
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default AttachmentPreviewDialog;
